import { create,StateCreator  } from 'zustand';
import {
    ActionTypeEnum,
    RowStateEnum,
    ToastModel,
    UnitModel,
    ValidationErrorModel,
    RequestActionModel,
    LookupTypeEnum
} from "../../models";
import {getActionMessage, getLabelName, reValidateLocalStorageCachedData} from "../../utils";
import {deleteUnit, getUnits, saveUnit} from "../../serviceBroker/unitApiServiceBroker";

//#region interface
// Define action button interface
interface ActionButton {
    text: string;
    onClick: () => void | Promise<void>;
}
// Define the store state interface
interface UnitState {
    // State
    loading: boolean;
    validationErrors: ValidationErrorModel[];
    unit: UnitModel;
    toastModel: ToastModel;
    pageName: any;
    showDeleteUnitModel: boolean;
    isRefresh: boolean;
    initialValues: UnitModel;

    // Setters
    setLoading: (loading: boolean) => void;
    setValidationErrors: (validationErrors: ValidationErrorModel[]) => void;
    setUnit: (unit: UnitModel) => void;
    setToastModel: (toastModel: ToastModel) => void;
    setPageName: (pageName: any) => void;
    setShowDeleteUnitModel: (showDeleteUnitModel: boolean) => void;
    setIsRefresh: (isRefresh: boolean) => void;

    // Business Logic
    getAllUnits: () => Promise<UnitModel[]>;
    handleSubmitUnit: (formData: UnitModel) => Promise<boolean>;
    filterUnits: (units: UnitModel[], searchValue: string) => UnitModel[];
    handleActions: (request: RequestActionModel) => Promise<void>;
    deleteUnitActions: ActionButton[];
}
//#endregion
//#region initial values
// Initial model values
const initialUnitValues: UnitModel = {
    IsDefault: false,
    Name_En: "",
    Name: "",
    ID: 0,
    CreatedBy: 0,
    ModifiedBy: 0,
    Errors: [],
    rowState: Number(RowStateEnum.Add),
};
//#endregion
//#region helper functions
const getErrorMessage = (error: unknown): string => {
    return error instanceof Error
        ? error.message
        : typeof error === 'string'
            ? error
            : 'An unknown error occurred';
};
//#endregion

// Create the store with typed StateCreator
const createUnitStore: StateCreator<UnitState> = (set, get) => ({
    // State
    loading: false,
    validationErrors: [],
    unit: { ...initialUnitValues },
    toastModel: {
        show: false,
        body: "process completed successfully",
        variant: "success",
    },
    pageName: null,
    showDeleteUnitModel: false,
    isRefresh: false,
    initialValues: initialUnitValues,

    // Setters
    setLoading: (loading) => set({ loading }),
    setValidationErrors: (validationErrors) => set({ validationErrors }),
    setUnit: (unit) => set({ unit }),
    setToastModel: (toastModel) => set({ toastModel }),
    setPageName: (pageName) => set({ pageName }),
    setShowDeleteUnitModel: (showDeleteUnitModel) => set({ showDeleteUnitModel }),
    setIsRefresh: (isRefresh) => set({ isRefresh }),
    // Business Logic
    // Gets all units from API
    getAllUnits: async () => {
        set({ loading: true });
        try {
            const unitList = await getUnits();
            return unitList?.Result || [];
        } catch (error) {
            console.error("Error fetching units:", error);
            return [];
        } finally {
            set({ loading: false });
        }
    },
    // Handle unit form submission (add/update)
    handleSubmitUnit: async (formData) => {
        const state = get();

        try {
            set({ loading: true });

            // Set the row state based on whether it's an add or update operation
            formData.rowState = formData.ID === 0
                ? Number(RowStateEnum.Add)
                : Number(RowStateEnum.Update);

            const res = await saveUnit(formData);

            if (res != null && res.Errors != null && res.Errors.length !== 0) {
                set({ validationErrors: res.Errors, loading: false });

                await state.handleActions({
                    id: 0,
                    action: ActionTypeEnum.Failed,
                    request: res.Errors,
                    requestBeforeError: formData,
                });

                return false;
            } else {
                set({ validationErrors: [], loading: false });

                await reValidateLocalStorageCachedData(LookupTypeEnum.Units);

                await state.handleActions({
                    id: formData.ID === 0 ? ActionTypeEnum.Add : ActionTypeEnum.Update,
                    action: ActionTypeEnum.AddSuccess,
                });

                return true;
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            set({
                loading: false,
                validationErrors: [{ MessageAr: errorMessage, MessageEn: errorMessage }]
            });
            return false;
        }
    },
    // Filter units by search text
    filterUnits: (units, searchValue) => {
        if (!searchValue || !searchValue.trim()) {
            return units;
        }

        const normalizedSearchValue = searchValue.toLowerCase().trim();

        return units.filter((unit) =>
            (unit.Name || "").toLowerCase().trim().includes(normalizedSearchValue) ||
            (unit.Name_En || "").toLowerCase().trim().includes(normalizedSearchValue)
        );
    },
    // Handle various actions in the unit module
    handleActions: async (request) => {
        const state = get();

        switch (request.action) {
            case ActionTypeEnum.CloseModelPopup:
                set({
                    validationErrors: [],
                    showDeleteUnitModel: false,
                    isRefresh: false
                });
                break;

            case ActionTypeEnum.AddSuccess:
            case ActionTypeEnum.Success:
                set({ validationErrors: [], loading: true });

                set({
                    unit: state.initialValues,
                    isRefresh: true,
                    toastModel: {
                        ...state.toastModel,
                        variant: "success",
                        body:getActionMessage(request.id as ActionTypeEnum),
                        show: true,
                    },
                    loading: false
                });
                break;

            case ActionTypeEnum.Clear:
                set({ loading: true });

                set({
                    validationErrors: [],
                    unit: state.initialValues,
                    isRefresh: true,
                    loading: false
                });
                break;

            case ActionTypeEnum.Update:
                window.scrollTo(0, 0);
                set({
                    validationErrors: [],
                    unit: request.request
                });
                break;

            case ActionTypeEnum.Delete:
                set({
                    validationErrors: [],
                    unit: request.request,
                    showDeleteUnitModel: true
                });
                break;

            case ActionTypeEnum.Failed:
                set({
                    validationErrors: request.request ?? [],
                    unit: request.requestBeforeError
                });
                break;

            case ActionTypeEnum.Refresh:
                set({ isRefresh: request.request });
                break;
        }
    },
    // Delete action buttons for the confirmation dialog
    get deleteUnitActions() {
        const state = get();

        return [
            {
                text: getLabelName("yes"),
                onClick: async () => {
                    const currentState = get();
                    set({ loading: true, showDeleteUnitModel: false });

                    try {
                        const result = await deleteUnit(currentState.unit.ID);
                        const isSuccess = !(result.Errors != null && result.Errors.length !== 0);

                        set({ loading: false });

                        if (isSuccess) {
                            await currentState.handleActions({
                                id: ActionTypeEnum.Delete,
                                action: ActionTypeEnum.Success,
                            });
                        } else {
                            set({ validationErrors: result?.Errors || [] });
                        }
                    } catch (error) {
                        const errorMessage = getErrorMessage(error);
                        set({
                            loading: false,
                            validationErrors: [{ MessageAr: errorMessage, MessageEn: errorMessage }]
                        });
                    }
                }
            },
            {
                text: getLabelName("no"),
                onClick: () => {
                    const currentState = get();
                    set({
                        unit: currentState.initialValues,
                        showDeleteUnitModel: false
                    });
                }
            }
        ];
    }
});

// Create the store with proper typing
export const useUnitManager = create<UnitState>(createUnitStore);

export default useUnitManager;
